/**
 * EVENTSFLO-JOB-REQUEST-SOURCE-ARCHIVE-V1
 *
 * Admin-only Job Request Source Archive — durable plain-text/markdown
 * snapshots of intake material from all sources (public quote, client
 * portal, future email/SMS/phone notes). Read-mostly. Manual phone-note
 * entry supported.
 */

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, FileText, Download, Copy, Plus, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
// EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1
import { GuideButton } from "../components/common/GuideButton";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "eventsflo_auth_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function SourceTypeBadge({ type }) {
  const map = {
    public_quote: "bg-blue-500/15 text-blue-300 border-blue-500/30",
    client_portal: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    email: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    inbox_paste: "bg-violet-500/15 text-violet-300 border-violet-500/30",
    phone_note: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    sms_note: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    whatsapp_note: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    manual: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  };
  return (
    <Badge variant="outline" className={`${map[type] || map.manual}`}>
      {type}
    </Badge>
  );
}

function ArchiveList({ items, onSelect }) {
  if (!items.length) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="jrs-empty">
        No archive entries yet.
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="jrs-list">
      {items.map((row) => (
        <button
          key={row.id}
          onClick={() => onSelect(row.id)}
          className="w-full text-left rounded-md border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-colors px-3 py-2"
          data-testid={`jrs-list-item-${row.id}`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <SourceTypeBadge type={row.source_type} />
                {row.is_test && (
                  <Badge variant="outline" className="text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/30">
                    TEST
                  </Badge>
                )}
                <span className="text-sm font-semibold truncate">{row.title}</span>
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">
                {[row.requester_name, row.client_name_snapshot, row.venue_name_snapshot]
                  .filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground shrink-0">
              {row.event_date_start || ""}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function ArchiveDetail({ archiveId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/admin/job-request-sources/${archiveId}`,
          { headers: authHeaders() }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setErr(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [archiveId]);

  async function handleCopy() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.plain_text || "");
      toast.success("Copied plain text to clipboard.");
    } catch {
      toast.error("Clipboard not available.");
    }
  }

  function handleDownload(fmt) {
    const url = `${API_URL}/api/admin/job-request-sources/${archiveId}/download?fmt=${fmt}`;
    const t = localStorage.getItem(TOKEN_KEY);
    fetch(url, { headers: t ? { Authorization: `Bearer ${t}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        const obj = URL.createObjectURL(blob);
        a.href = obj;
        a.download = `${(data?.title || "archive").slice(0, 64)}.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(obj);
      })
      .catch((e) => toast.error(`Download failed: ${e.message}`));
  }

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }
  if (err) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{err}</AlertDescription>
      </Alert>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="jrs-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="jrs-back-btn">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to list
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <SourceTypeBadge type={data.source_type} />
            {data.is_test && (
              <Badge variant="outline" className="bg-rose-500/15 text-rose-300 border-rose-500/30">TEST</Badge>
            )}
            <span>{data.title}</span>
          </CardTitle>
          <CardDescription className="text-xs">
            Source: <span className="font-mono">{data.source_record_collection}</span>{" "}
            · ID: <span className="font-mono">{data.source_record_id}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="text-muted-foreground">Requester:</span> {data.requester_name || "—"}</div>
            <div><span className="text-muted-foreground">Email:</span> {data.requester_email || "—"}</div>
            <div><span className="text-muted-foreground">Phone:</span> {data.requester_phone || "—"}</div>
            <div><span className="text-muted-foreground">Client:</span> {data.client_name_snapshot || "—"}</div>
            <div><span className="text-muted-foreground">Venue:</span> {data.venue_name_snapshot || "—"}</div>
            <div><span className="text-muted-foreground">Start:</span> {data.event_date_start || "—"}</div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={handleCopy} data-testid="jrs-copy-btn">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy plain text
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleDownload("md")} data-testid="jrs-download-md-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Download .md
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleDownload("txt")} data-testid="jrs-download-txt-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Download .txt
            </Button>
          </div>

          <div>
            <div className="text-xs text-muted-foreground mb-1">Markdown</div>
            <pre className="text-xs bg-muted/30 rounded-md p-3 overflow-x-auto whitespace-pre-wrap" data-testid="jrs-markdown">
              {data.markdown_text}
            </pre>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ManualCreateDialog({ open, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    source_type: "phone_note",
    title: "",
    body_text: "",
    requester_name: "",
    requester_email: "",
    requester_phone: "",
    venue_name: "",
    event_date_start: "",
  });

  if (!open) return null;

  function setField(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit() {
    if (!form.title.trim() || !form.body_text.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/job-request-sources/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success("Archive entry created.");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(`Create failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="jrs-manual-dialog">
      <Card className="w-full max-w-lg mx-4">
        <CardHeader>
          <CardTitle className="text-base">Add manual job request note</CardTitle>
          <CardDescription className="text-xs">
            Use this for phone / SMS / WhatsApp / pasted intake. The body is
            preserved verbatim; nothing else is created.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <label className="text-xs text-muted-foreground">Source type</label>
            <select
              className="w-full mt-1 bg-background border border-border/40 rounded-md px-2 py-1"
              value={form.source_type}
              onChange={(e) => setField("source_type", e.target.value)}
              data-testid="jrs-manual-source-type"
            >
              {["phone_note", "sms_note", "whatsapp_note", "inbox_paste", "manual", "email"].map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <Input
            placeholder="Title (use TEST prefix for test entries)"
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            data-testid="jrs-manual-title"
          />
          <Textarea
            placeholder="Paste the request body / call notes / SMS verbatim…"
            rows={8}
            value={form.body_text}
            onChange={(e) => setField("body_text", e.target.value)}
            data-testid="jrs-manual-body"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Requester name" value={form.requester_name}
                   onChange={(e) => setField("requester_name", e.target.value)} />
            <Input placeholder="Requester email" value={form.requester_email}
                   onChange={(e) => setField("requester_email", e.target.value)} />
            <Input placeholder="Requester phone" value={form.requester_phone}
                   onChange={(e) => setField("requester_phone", e.target.value)} />
            <Input placeholder="Venue (snapshot)" value={form.venue_name}
                   onChange={(e) => setField("venue_name", e.target.value)} />
            <Input placeholder="Event start (YYYY-MM-DD)" value={form.event_date_start}
                   onChange={(e) => setField("event_date_start", e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={busy} data-testid="jrs-manual-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              Create archive entry
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminJobRequestArchivePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterTest, setFilterTest] = useState("all");
  const [manualOpen, setManualOpen] = useState(false);

  async function loadList() {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    params.set("limit", "200");
    if (filterType !== "all") params.set("source_type", filterType);
    if (filterTest !== "all") params.set("is_test", filterTest);
    if (search.trim()) params.set("q", search.trim());
    try {
      const r = await fetch(
        `${API_URL}/api/admin/job-request-sources?${params}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setItems(body.items || []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterTest]);

  return (
    <div className="space-y-4" data-testid="admin-job-request-archive-page">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Job Request Archive
          </h1>
          <p className="text-xs text-muted-foreground">
            Durable original-source snapshots from every intake channel.
            Survives downstream cleanup. Admin-only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* EVENTSFLO-GUIDE-BUTTON-ROLLOUT-V1 */}
          <GuideButton testId="job-request-archive-guide-btn" />
          <Button size="sm" onClick={() => setManualOpen(true)} data-testid="jrs-manual-open-btn">
            <Plus className="h-4 w-4 mr-1" />
            Add manual note
          </Button>
        </div>
      </div>

      {selectedId ? (
        <ArchiveDetail
          archiveId={selectedId}
          onBack={() => setSelectedId(null)}
        />
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                placeholder="Search title / requester / client / body…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadList()}
                className="max-w-xs"
                data-testid="jrs-search-input"
              />
              <select
                className="bg-background border border-border/40 rounded-md px-2 py-1 text-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                data-testid="jrs-filter-type"
              >
                <option value="all">All sources</option>
                {["public_quote", "client_portal", "email", "inbox_paste",
                  "phone_note", "sms_note", "whatsapp_note", "manual"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <select
                className="bg-background border border-border/40 rounded-md px-2 py-1 text-sm"
                value={filterTest}
                onChange={(e) => setFilterTest(e.target.value)}
                data-testid="jrs-filter-test"
              >
                <option value="all">All</option>
                <option value="false">Real only</option>
                <option value="true">TEST only</option>
              </select>
              <Button size="sm" variant="outline" onClick={loadList} data-testid="jrs-search-btn">
                Search
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <ArchiveList items={items} onSelect={setSelectedId} />
            )}
          </CardContent>
        </Card>
      )}

      <ManualCreateDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={() => loadList()}
      />
    </div>
  );
}
